import { COLOR } from "../../common/Color"
import { FontType } from "../../common/TextUtils"
import { colorNum } from "../utils/ColorUtils"
import { PhaserText } from "../utils/PhaserText"
import { Scale } from "../utils/Scale"
import { Buttons } from "./Buttons"
import { Circle } from "./Shape"

interface RadioButtonInfo {
    radio: Circle
    radioSelected: Circle
    textSprite?: Phaser.Text
    onClick: () => void
}

const radioWidth = () => Scale.x(0.02)

export class RadioButton {
    info: RadioButtonInfo
    originalColor: string
    private currentlySelected: boolean

    constructor(
        private game: Phaser.Game,
        private group: Phaser.Group,
        onSelect: () => void,
        x: number,
        y: number,
        enabled = false,
        option?: RadioOption,
        fontPx = 18,
        type = FontType.BODY
    ) {
        const bulletY = y + fontPx * 0.85
        const radio = new Circle(game, x, bulletY, radioWidth(), {
            lineWidth: 1,
            fillColor: colorNum.white,
            group
        })
        const radioSelected = new Circle(game, x, bulletY, radioWidth() / 2, {
            lineWidth: 0,
            fillColor: colorNum.red,
            group
        })
        const onClick = () => {
            if (option && option.audioKey) {
                game.sound.stopAll()
                game.add.audio(option.audioKey).play()
            }
            this.selected = true
            onSelect()
        }
        let textSprite: Phaser.Text
        if (option) {
            this.originalColor = option.color ? option.color : COLOR.black
            textSprite = PhaserText.create(game, option.text, {
                x: x + radio.invisibleSprite.width + Scale.x(0.01),
                y,
                group,
                fontPx,
                type,
                color: this.originalColor,
                wordWrapWidth: option.wordWrapWidth
            })
        }
        this.info = {radio, radioSelected, onClick, textSprite}
        this.selected = false
        if (enabled) {
            this.enable()
        }
    }

    get selected(): boolean {
        return this.currentlySelected
    }

    set selected(s: boolean) {
        this.currentlySelected = s
        this.info.radioSelected.graphics.visible = s
    }

    enable = () => {
        const {radio, textSprite, onClick} = this.info
        Buttons.makeSpriteButton(radio.invisibleSprite, onClick)
        if (textSprite) {
            Buttons.makeSpriteButton(textSprite, onClick)
            textSprite.addColor(this.originalColor, 0)
        }
    }

    disable = (color?: string) => {
        const {radio, textSprite} = this.info
        Buttons.undoSpriteButton(radio.invisibleSprite)
        if (textSprite) {
            Buttons.undoSpriteButton(textSprite)
            textSprite.addColor(color ? color : COLOR.gray, 0)
        }
    }

    setVisible = (visible: boolean) => {
        const {radio, radioSelected, textSprite} = this.info
        radio.graphics.visible = visible

        if (this.selected && !visible) {
            // If button has been selected, and we want to hide the button:
            radioSelected.graphics.visible = false
        } else if (this.selected && visible && !radioSelected.graphics.visible) {
            // If button has been selected, we want to show it, and the select is currently invisible:
            radioSelected.graphics.visible = true
        }

        if (textSprite) {
            textSprite.visible = false
        }

        if (!visible) {
            this.disable()
        }
    }

    destroy = () => {
        const {radio, radioSelected, textSprite} = this.info
        radio.destroy()
        radioSelected.destroy()
        if (textSprite) {
            textSprite.destroy()
        }
    }
}

export interface RadioOption {
    text: string
    audioKey?: string
    color?: string
    wordWrapWidth? : number
}

export class RadioGroup {
    currentlySelected?: string
    currentlySelectedIndex?: number
    group: Phaser.Group
    buttons: Array<RadioButton>

    constructor(
        game: Phaser.Game,
        x: number,
        y: number,
        options: Array<RadioOption>,
        selectionPerformed: () => void,
        enabled = true,
        horizontal = false,
        fontPx = 18,
        group?: Phaser.Group,
        type: FontType = FontType.BODY
    ) {
        let currentX = x + (radioWidth() / 2)
        let currentY = y
        this.group = group ? group : game.add.group()
        this.buttons = options.map((option, i) => {
            const button = new RadioButton(game, this.group, () => {
                this.currentlySelected = option.text
                this.currentlySelectedIndex = i
                this.buttons.forEach(b => b.selected = false)
                button.selected = true
                selectionPerformed()
            }, currentX, currentY, enabled, option, fontPx, type)

            if (horizontal) {
                currentX += button.info.textSprite.width + Scale.x(0.08)
            } else {
                currentY += button.info.textSprite.height + Scale.y(0.01)
            }

            return button
        })
    }

    enable = () => this.buttons.forEach(button => button.enable())

    disable = (color?: string) => this.buttons.forEach(button => button.disable(color))

    destroy = () => this.buttons.forEach(button => button.destroy())
}
